import useSWR from 'swr';
import api from '../../../../services/api';
import { formatDate } from '../../../../config/appConfig';
import { emailDetailPath } from '../../../emails/utils/emailPartitionAtQuery';
import type { ActivityLog } from '../../../activity-logs/useData';
import type { TimelineEvent } from './types';

function toRecordList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown[] }).data)) {
    return (raw as { data: Record<string, unknown>[] }).data;
  }
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: unknown[] }).records)) {
    return (raw as { records: Record<string, unknown>[] }).records;
  }
  return [];
}

function emailFromField(from: unknown): string | null {
  if (Array.isArray(from) && from.length) {
    const first = from[0] as { email?: string; name?: string };
    return first?.name ?? first?.email ?? null;
  }
  if (from && typeof from === 'object' && 'email' in (from as object)) {
    return (from as { email?: string; name?: string }).name ?? (from as { email?: string }).email ?? null;
  }
  return null;
}

function mapEmails(records: Record<string, unknown>[]): TimelineEvent[] {
  return records.map((r) => {
    const id = r.id as number;
    const partitionAt = r.partitionAt as string | undefined;
    const subject = (r.subject as string)?.trim() || '(No subject)';
    const date = String(r.date ?? '');
    return {
      id: `email-${id}-${partitionAt ?? ''}`,
      type: 'email' as const,
      date,
      title: subject,
      subtitle: emailFromField(r.from) ?? undefined,
      link: emailDetailPath(id, partitionAt),
    };
  });
}

function mapAppointments(records: Record<string, unknown>[]): TimelineEvent[] {
  return records.map((r) => {
    const id = r.clientAppId as number;
    const name = (r.clientAppName as string)?.trim() || `Appointment #${id}`;
    const date = String(r.clientAppScheduleDateTime ?? r.clientAppCreatedAt ?? '');
    const status = (r.clientAppStatusDescr as { as_descr?: string } | undefined)?.as_descr;
    return {
      id: `appointment-${id}`,
      type: 'appointment' as const,
      date,
      title: name,
      subtitle: status ?? undefined,
      link: `/appointments/${id}`,
    };
  });
}

function mapTodos(records: Record<string, unknown>[]): TimelineEvent[] {
  return records.map((r) => {
    const id = r.todoId as number;
    const descr = (r.todoDescr as string)?.trim() || `Todo #${id}`;
    const date = String(r.todoDueDate ?? r.todoCreatedAt ?? '');
    const completed = Boolean(r.todoCompleted);
    return {
      id: `todo-${id}`,
      type: 'todo' as const,
      date,
      title: descr.length > 80 ? `${descr.slice(0, 80)}…` : descr,
      subtitle: completed ? 'Completed' : r.todoDueDate ? `Due ${formatDate(String(r.todoDueDate))}` : undefined,
      link: `/todos/${id}`,
      meta: { completed },
    };
  });
}

function mapActivityLogs(logs: ActivityLog[]): TimelineEvent[] {
  return logs.map((log) => {
    const userName =
      log.user?.userDisplayName ||
      `${log.user?.userFirstName ?? ''} ${log.user?.userLastName ?? ''}`.trim() ||
      log.user?.userEmail ||
      'Unknown';
    const actionLabels: Record<string, string> = {
      CREATE: 'Created',
      UPDATE: 'Updated',
      DELETE: 'Deleted',
      LOGIN: 'Logged in',
      SESSION_END: 'Session ended',
    };
    return {
      id: `activity-${log.id}`,
      type: 'activity' as const,
      date: log.createdAt,
      title: actionLabels[log.action] ?? log.action,
      subtitle: userName,
      link: `/activity-logs/${log.id}`,
      meta: { action: log.action, userName },
    };
  });
}

function sortByDateDesc(events: TimelineEvent[]): TimelineEvent[] {
  return [...events].sort((a, b) => {
    const ta = a.date ? new Date(a.date).getTime() : 0;
    const tb = b.date ? new Date(b.date).getTime() : 0;
    return tb - ta;
  });
}

async function fetchClient360Timeline(clientId: number): Promise<TimelineEvent[]> {
  const [emailsRes, appointmentsRes, todosRes, activityRes] = await Promise.all([
    api.get(`/emails/linked_to_client/${clientId}`),
    api.get(`/crm-clients-appointments/all_of/${clientId}`),
    api.get(`/crm-todos/all_of/${clientId}`),
    api.get(`/activity-logs/entity/Client/${clientId}`),
  ]);

  const events = [
    ...mapEmails(toRecordList(emailsRes.data)),
    ...mapAppointments(toRecordList(appointmentsRes.data)),
    ...mapTodos(toRecordList(todosRes.data)),
    ...mapActivityLogs(Array.isArray(activityRes.data) ? activityRes.data : []),
  ];

  return sortByDateDesc(events);
}

export function useClient360Timeline(clientId: number | null | undefined) {
  const key = clientId ? `client360-timeline-${clientId}` : null;
  const { data, error, isLoading, mutate } = useSWR<TimelineEvent[]>(
    key,
    () => fetchClient360Timeline(clientId!),
    { revalidateOnFocus: true, dedupingInterval: 30_000 },
  );

  return {
    events: data ?? [],
    isLoading,
    isError: error,
    mutate,
  };
}
